import type { KLineData } from 'klinecharts'

/** Neutral color for an unchanged bar, when the caller doesn't override it. */
const DEFAULT_CHANGE_FLAT = '#888888'

/** Up/down/flat colors for the change field. The caller passes the SAME up/down
 *  the candles use (settings.candleUpColor / candleDownColor), so the change
 *  value tracks the candle color instead of a hardcoded green/red. */
export interface ChangeColors {
  up: string
  down: string
  /** Optional; defaults to a neutral gray. */
  flat?: string
}

/**
 * Standard change field appended to the OHLC tooltip: `+0.08 (+0.01%)` (no
 * label). Computed vs the previous bar's close — on a daily chart that's the
 * previous day's close; when there's no previous bar (the earliest available
 * data) it falls back to the current bar's open. Colored by direction using the
 * caller's up/down/flat colors (so it matches the candles). Amount precision
 * follows the price magnitude (sub-$1 → 4 decimals).
 */
export function barChangeTooltipEntry(
  prev: KLineData | null | undefined,
  current: KLineData,
  colors: ChangeColors,
): { title: string; value: { text: string; color: string } } {
  const ref = prev?.close ?? current.open
  const change = current.close - ref
  const pct = ref !== 0 ? (change / ref) * 100 : 0
  const decimals = Math.abs(current.close) >= 1 ? 2 : 4
  const sign = change > 0 ? '+' : change < 0 ? '-' : ''
  const abs = Math.abs(change)
  const text = `${sign}${abs.toFixed(decimals)} (${sign}${Math.abs(pct).toFixed(2)}%)`
  const flat = colors.flat ?? DEFAULT_CHANGE_FLAT
  const color = change > 0 ? colors.up : change < 0 ? colors.down : flat
  // No label — the value rides directly after the C: close.
  return { title: '', value: { text, color } }
}
